import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import {
    Box,
    VStack,
    Text,
    Divider,
    Image,
    Flex,
    Icon,
    Badge,
    Collapse,
    useColorModeValue,
    Drawer,
    DrawerContent,
    DrawerOverlay,
    DrawerCloseButton,
    DrawerBody
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    MdDashboard,
    MdPeople,
    MdDescription,
    MdMap,
    MdAssessment,
    MdNotifications,
    MdSupervisorAccount,
    MdEventNote,
    MdBugReport,
    MdAnalytics,
    MdAppSettingsAlt,
    MdAppRegistration,
    MdNetworkCheck,
    MdExpandMore,
    MdExpandLess,
    MdFolder,
    MdStorage,
    MdBuild,
    MdAdminPanelSettings,
    MdChecklist,
    MdPlaylistAddCheck,
    MdDynamicForm,
    MdAltRoute,
    MdBarChart,
    MdStorefront
} from 'react-icons/md';

/**
 * Sidebar Navigation Component
 */
function Sidebar({ isOpen, onClose, variant, userProfile, appConfig, issuesStats, ...rest }) {
    const location = useLocation();
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);
    const scrollPosition = useRef(0);
    const observerRef = useRef(null);

    // Track which dropdown groups are open (default all open)
    const [openGroups, setOpenGroups] = useState({
        clients: false,
        reports: false,
        applications: false,
        devops: false,
    });

    const toggleGroup = (key) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // ── Scroll lock ──────────────────────────────────────────────────────────
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        observerRef.current = new MutationObserver(() => {
            if (container.scrollTop !== scrollPosition.current)
                container.scrollTop = scrollPosition.current;
        });
        observerRef.current.observe(container, { childList: true, subtree: true, attributes: true });

        const ro = new ResizeObserver(() => {
            if (container.scrollTop !== scrollPosition.current)
                container.scrollTop = scrollPosition.current;
        });
        ro.observe(container);

        return () => { observerRef.current?.disconnect(); ro.disconnect(); };
    }, []);

    const handleScroll = (e) => { scrollPosition.current = e.target.scrollTop; };

    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        container.scrollTop = scrollPosition.current;
        requestAnimationFrame(() => {
            container.scrollTop = scrollPosition.current;
            requestAnimationFrame(() => { container.scrollTop = scrollPosition.current; });
        });
    }, [location.pathname]);

    // ── Theme colors ─────────────────────────────────────────────────────────
    const sidebarBg      = useColorModeValue('white', 'gray.800');
    const borderColor    = useColorModeValue('gray.200', 'gray.700');
    const textColor      = useColorModeValue('gray.700', 'gray.200');
    const headingColor   = useColorModeValue('gray.900', 'white');
    const activeColor    = useColorModeValue('brand.500', 'brand.300');
    const hoverBg        = useColorModeValue('gray.50', 'gray.700');
    const groupHeaderBg  = useColorModeValue('gray.50', 'gray.750');
    const groupHeaderHov = useColorModeValue('gray.100', 'gray.700');
    const scrollbarTrack       = useColorModeValue('#f1f1f1', '#2d3748');
    const scrollbarThumb       = useColorModeValue('#888', '#4a5568');
    const scrollbarThumbHover  = useColorModeValue('#555', '#718096');

    // ── Badge helpers ─────────────────────────────────────────────────────────
    const getBadgeContent = (item) => {
        if (item.badge === 'issuesStats' && issuesStats)
            return issuesStats.openIssues > 0 ? issuesStats.openIssues : null;
        return null;
    };

    const getBadgeColorScheme = (n) => {
        if (n > 50) return 'red';
        if (n > 20) return 'orange';
        if (n > 10) return 'yellow';
        if (n > 0)  return 'blue';
        return 'green';
    };

    // ── Navigation data ───────────────────────────────────────────────────────

    /** Top-level PM Analytics items (flat, no dropdown) */
    const analyticsItems = [
        { name: 'Dashboard', icon: MdDashboard, path: '/dashboard',  description: 'Overview and statistics' },
        { name: 'Analytics',  icon: MdAnalytics,  path: '/analytics',  description: 'Client heartbeat and issues statistics' },
        { name: 'Issues',     icon: MdBugReport,  path: '/issues',     description: 'Report triggered issue management', badge: 'issuesStats' },
        { name: 'Checklist Results', icon: MdPlaylistAddCheck, path: '/checklist-results', description: 'Client CheckList Results' },
       { name: 'Dynamic Report Results', icon: MdBarChart, path: '/dynamic-report-results', description: 'Client dynamic report execution results' },
       { name: 'Market Data ETL Monitor', icon: MdStorefront,       path: '/market-data-results',     description: 'Market data ETL run status per client' },
    ];

    /**
     * Admin Console grouped into collapsible sections.
     * Each group: { key, label, icon, items[] }
     */
    const adminGroups = [
        {
            key: 'clients',
            label: 'Clients',
            icon: MdPeople,
            items: [
                { name: 'Clients',         icon: MdPeople,           path: '/clients',         description: 'Manage client configurations' },
            ],
        },
        {
            key: 'reports',
            label: 'Reports',
            icon: MdStorage,
            items: [
                { name: 'Reports',         icon: MdDescription, path: '/reports',        description: 'Manage report definitions' },
                { name: 'Map Reports',     icon: MdMap,          path: '/map-reports',    description: 'Configure client-report mappings' },
                { name: 'Report Results',  icon: MdAssessment,   path: '/report-results', description: 'View execution results' },
                { name: 'Dynamic Reports', icon: MdDynamicForm, path: '/dynamic-reports',         description: 'Manage dynamic report definitions' },
                { name: 'Dynamic Report Mappings', icon: MdAltRoute,    path: '/dynamic-report-mappings',  description: 'Map dynamic reports to clients' },
                { name: 'Notifications',   icon: MdNotifications,    path: '/notifications',   description: 'System alerts and messages' },
            ],
        },
        {
            key: 'checklist',
            label: 'Checklist',
            icon: MdChecklist,
            items: [
                { name: 'Checklist Manager',  icon: MdChecklist,     path: '/checklist-manager',  description: 'Manage checklist templates and items' },
                { name: 'Checklist Mappings', icon: MdPlaylistAddCheck, path: '/checklist-mappings', description: 'Map checklists to clients or reports' },
            ],
        },
        {
            key: 'applications',
            label: 'Applications',
            icon: MdAppSettingsAlt,
            items: [
                { name: 'Client Applications', icon: MdAppSettingsAlt,  path: '/client-applications', description: 'Manage client applications and integrations' },
                { name: 'Client App Mappings', icon: MdAppRegistration, path: '/client-appmappings',   description: 'Map applications to the client' },
            ],
        },
        {
            key: 'devops',
            label: 'DevOps',
            icon: MdBuild,
            items: [
                { name: 'Event Scheduling', icon: MdEventNote,    path: '/event-scheduling', description: 'Schedule deployment, upgrade and maintenance events' },
                { name: 'URL Manager',      icon: MdNetworkCheck, path: '/url-manager',      description: "Manage URLs that need monitoring" },
            ],
        },
        {
            key: 'users',
            label: 'Users',
            icon: MdPeople,
            items: [  
                { name: 'User Management', icon: MdSupervisorAccount, path: '/user-management', description: 'Manage user accounts and roles' },
            ],
        },
        
    ];

    useEffect(() => {
        const activeGroup = adminGroups.find(group =>
            group.items.some(item => item.path === location.pathname)
        );
        if (activeGroup) {
            setOpenGroups(prev => ({ ...prev, [activeGroup.key]: true }));
        }
    }, [location.pathname]);

    // ── Sub-components ────────────────────────────────────────────────────────

    /** Individual nav item (leaf) */
    const NavItem = ({ item, indented = false, onClick }) => {
        const isActive      = location.pathname === item.path;
        const badgeContent  = getBadgeContent(item);
        const badgeScheme   = getBadgeColorScheme(issuesStats?.openIssues || 0);

        const handleClick = (e) => {
            if (item.disabled) { e.preventDefault(); return; }
            if (scrollContainerRef.current)
                scrollPosition.current = scrollContainerRef.current.scrollTop;
            if (onClick) onClick(e);
            if (item.path !== location.pathname) navigate(item.path);
        };

        return (
            <Box
                as="button"
                onClick={handleClick}
                w="full"
                textAlign="left"
                bg="transparent"
                border="none"
                p={0}
                cursor={item.disabled ? 'not-allowed' : 'pointer'}
                opacity={item.disabled ? 0.5 : 1}
                _focus={{ outline: 'none' }}
            >
                <Flex
                    align="center"
                    p={3}
                    pl={indented ? 8 : 3}   /* indent children */
                    mx={2}
                    borderRadius="lg"
                    role="group"
                    bg={isActive ? activeColor : 'transparent'}
                    color={isActive ? 'white' : textColor}
                    _hover={!item.disabled ? {
                        bg: isActive ? activeColor : hoverBg,
                        color: isActive ? 'white' : headingColor,
                    } : {}}
                    transition="all 0.2s"
                >
                    <Icon mr={3} fontSize="18" as={item.icon} opacity={isActive ? 1 : 0.65} />
                    <Box flex="1">
                        <Text fontWeight={isActive ? '600' : '500'} fontSize="sm">
                            {item.name}
                        </Text>
                        {item.description && (
                            <Text fontSize="xs" opacity={0.7} display={{ base: 'none', xl: 'block' }}>
                                {item.description}
                            </Text>
                        )}
                    </Box>
                    {badgeContent && (
                        <Badge
                            colorScheme={badgeScheme}
                            variant="solid"
                            borderRadius="full"
                            fontSize="xs"
                            minW="20px"
                            h="20px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            ml={2}
                        >
                            {badgeContent}
                        </Badge>
                    )}
                </Flex>
            </Box>
        );
    };

    /** Collapsible group header */
    const GroupHeader = ({ group }) => {
        const isOpen        = openGroups[group.key];
        // Highlight header when any child route is active
        const hasActive     = group.items.some(i => location.pathname === i.path);
        const activeGroupBg = useColorModeValue('brand.50', 'brand.900');

        return (
            <Box>
                {/* Group toggle button */}
                <Flex
                    as="button"
                    align="center"
                    w="full"
                    px={4}
                    py={2}
                    bg={hasActive ? activeGroupBg : groupHeaderBg}
                    borderLeft={hasActive ? '3px solid' : '3px solid transparent'}
                    borderLeftColor={hasActive ? activeColor : 'transparent'}
                    _hover={{ bg: groupHeaderHov, cursor: 'pointer' }}
                    _focus={{ outline: 'none' }}
                    onClick={() => toggleGroup(group.key)}
                    transition="all 0.2s"
                >
                    <Icon
                        as={group.icon}
                        fontSize="16"
                        mr={2}
                        color={hasActive ? activeColor : textColor}
                        opacity={hasActive ? 1 : 0.7}
                    />
                    <Text
                        flex="1"
                        fontSize="xs"
                        fontWeight="700"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        color={hasActive ? activeColor : textColor}
                        textAlign="left"
                    >
                        {group.label}
                    </Text>
                    <Icon
                        as={isOpen ? MdExpandLess : MdExpandMore}
                        fontSize="18"
                        color={textColor}
                        opacity={0.6}
                    />
                </Flex>

                {/* Collapsible children */}
                <Collapse in={isOpen} animateOpacity>
                    <VStack spacing={1} align="stretch" py={1}>
                        {group.items.map(item => (
                            <NavItem key={item.path} item={item} indented onClick={onClose} />
                        ))}
                    </VStack>
                </Collapse>
            </Box>
        );
    };

    /** Section label (plain text heading, no toggle) */
    const SectionHeader = ({ title }) => (
        <Text
            fontSize="xs"
            fontWeight="bold"
            color={textColor}
            textTransform="uppercase"
            letterSpacing="wide"
            px={4}
            py={2}
            opacity={0.7}
        >
            {title}
        </Text>
    );

    // ── Main sidebar layout ───────────────────────────────────────────────────

    const SidebarContent = ({ onClose, isDrawer = false }) => (
        <Box
            bg={sidebarBg}
            borderRight={!isDrawer ? '1px' : 'none'}
            borderRightColor={borderColor}
            w="250px"
            h="100vh"
            maxH="100vh"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            {...rest}
        >
            {/* ── Header ── */}
            <Flex
                align="center"
                p={4}
                h="64px"
                borderBottom="1px"
                borderBottomColor={borderColor}
                flexShrink={0}
            >
                <Image src="prismicon96.png" alt="Prism Monitor" height="40px" width="auto" paddingRight={2} />
                <Box>
                    <Text fontSize="lg" fontWeight="bold" color={headingColor} lineHeight="shorter">
                        Prism Monitor
                    </Text>
                    <Text fontSize="xs" color={textColor} opacity={0.7}>
                        Analytics Dashboard Interface
                    </Text>
                </Box>
            </Flex>

            {/* ── Scrollable nav ── */}
            <Box
                ref={scrollContainerRef}
                flex="1"
                overflowY="scroll"
                overflowX="hidden"
                h="calc(100vh - 64px - 80px)"
                className="sidebar-scroll"
                onScroll={handleScroll}
                css={{
                    '&::-webkit-scrollbar':       { width: '6px', display: 'block' },
                    '&::-webkit-scrollbar-track': { background: scrollbarTrack, borderRadius: '4px', margin: '4px' },
                    '&::-webkit-scrollbar-thumb': {
                        background: scrollbarThumb, borderRadius: '4px',
                        border: `1px solid ${scrollbarTrack}`,
                        '&:hover': { background: scrollbarThumbHover },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
                    scrollBehavior: 'auto',
                }}
            >
                <VStack spacing={0} align="stretch" pt={4} pb={4}>

                    {/* PM Analytics — flat items */}
                    <Box mb={2}>
                        <SectionHeader title="PM Analytics" />
                        <VStack spacing={1} align="stretch" mt={1}>
                            {analyticsItems.map(item => (
                                <NavItem key={item.path} item={item} onClick={onClose} />
                            ))}
                        </VStack>
                    </Box>

                    <Divider borderColor={borderColor} mx={4} w="auto" />

                    {/* Admin Console — grouped dropdowns */}
                    <Box mt={2}>
                        <SectionHeader title="Admin Console" />
                        <VStack spacing={1} align="stretch" mt={1}>
                            {adminGroups.map(group => (
                                <GroupHeader key={group.key} group={group} />
                            ))}
                        </VStack>
                    </Box>

                </VStack>
            </Box>

            {/* ── Footer ── */}
            <Box
                p={4}
                borderTop="1px"
                borderTopColor={borderColor}
                flexShrink={0}
                minH="80px"
            >
                <Text fontSize="xs" color={textColor} opacity={0.6} textAlign="center">
                    {appConfig?.app?.name || 'Prism Monitor'} v{appConfig?.app?.version || '1.0.0'}
                </Text>
                {userProfile?.email && (
                    <Text fontSize="xs" color={textColor} opacity={0.6} textAlign="center" mt={1} noOfLines={1}>
                        {userProfile.email}
                    </Text>
                )}
                {issuesStats?.openIssues > 0 && (
                    <Flex justify="center" mt={2}>
                        <Badge
                            colorScheme={getBadgeColorScheme(issuesStats.openIssues)}
                            variant="subtle"
                            borderRadius="full"
                            fontSize="xs"
                            px={2}
                        >
                            {issuesStats.openIssues} open issue{issuesStats.openIssues === 1 ? '' : 's'}
                        </Badge>
                    </Flex>
                )}
            </Box>
        </Box>
    );

    // Mobile drawer
    if (variant === 'drawer') {
        return (
            <Drawer
                autoFocus={false}
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                returnFocusOnClose={false}
                onOverlayClick={onClose}
                size="xs"
            >
                <DrawerOverlay bg="blackAlpha.300" />
                <DrawerContent maxW="250px">
                    <DrawerCloseButton position="absolute" right={4} top={4} zIndex={10} size="md" />
                    <DrawerBody p={0}>
                        <SidebarContent onClose={onClose} isDrawer={true} />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        );
    }

    return <SidebarContent onClose={onClose} isDrawer={false} />;
}

export default Sidebar;